
import Splide from "//cuddleclones.com/cdn/shop/t/512/assets/splide.esm.js?v=42432906053591983441783670548";
import {
    surgeInitProductGalleryLightbox,
    surgeScanProductGalleryLightboxes
} from "//cuddleclones.com/cdn/shop/t/512/assets/product-gallery-lightbox.js?v=18103221180217568521783670548";

/**
 * Keeps the active thumbnail visible in its scroll rail: aligns the slide to the
 * leading edge of the nearest scroll ancestor (top for vertical stacks, start for horizontal strips).
 *
 * @param {HTMLElement | null | undefined} switchSliderRoot — element with `[data-switcher-thumbs]` (splide root)
 * @param {object | null | undefined} thumbsSplide — mounted Splide instance for `[data-switcher-thumbs]`
 */
function surgeScrollSplideSwitcherActiveToLeadingEdge(switchSliderRoot, thumbsSplide) {
    if (!switchSliderRoot || !thumbsSplide?.Components?.Slides) return;
    const idx = thumbsSplide.index;
    const slideObj = thumbsSplide.Components.Slides.getAt(idx);
    const slideEl = slideObj?.slide;
    if (!slideEl || !slideEl.isConnected) return;

    /** @type {HTMLElement | null} */
    let ancestor = slideEl.parentElement;
    while (ancestor && ancestor !== switchSliderRoot && ancestor !== document.documentElement) {
        const cs = window.getComputedStyle(ancestor);
        const oy = cs.overflowY;
        const ox = cs.overflowX;
        const scrollY =
            (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
            ancestor.scrollHeight > ancestor.clientHeight + 1;
        const scrollX =
            (ox === 'auto' || ox === 'scroll' || ox === 'overlay') &&
            ancestor.scrollWidth > ancestor.clientWidth + 1;
        if (scrollY) {
            const r = slideEl.getBoundingClientRect();
            const a = ancestor.getBoundingClientRect();
            const top = ancestor.scrollTop + (r.top - a.top);
            ancestor.scrollTo({ top, behavior: 'smooth' });
            return;
        }
        if (scrollX) {
            const r = slideEl.getBoundingClientRect();
            const a = ancestor.getBoundingClientRect();
            const left = ancestor.scrollLeft + (r.left - a.left);
            ancestor.scrollTo({ left, behavior: 'smooth' });
            return;
        }
        ancestor = ancestor.parentElement;
    }

}

/**
 * Prev/next on the main PDP gallery: disabled only on first / last slide.
 *
 * @param {HTMLElement} host — `splide-slider`
 * @param {import('@splidejs/splide').Splide} mainSplide
 * @param {import('@splidejs/splide').Splide | null | undefined} thumbSplide
 */
function surgeBindProductGalleryArrows(host, mainSplide, thumbSplide) {
    const galleryPrev = host.querySelector('[data-gallery-prev]');
    const galleryNext = host.querySelector('[data-gallery-next]');
    if (!galleryPrev || !galleryNext || !mainSplide) return;

    if (host._surgeGalleryArrowsAbort) {
        host._surgeGalleryArrowsAbort.abort();
    }
    const ac = new AbortController();
    host._surgeGalleryArrowsAbort = ac;
    const { signal } = ac;

    const updateGalleryArrows = (indexHint) => {
        const idx =
            typeof indexHint === 'number' && indexHint >= 0 ? indexHint : mainSplide.index;
        const end =
            mainSplide.Components?.Controller?.getEnd?.() ??
            Math.max(0, mainSplide.length - 1);
        const atStart = idx <= 0;
        const atEnd = idx >= end;
        galleryPrev.disabled = atStart;
        galleryNext.disabled = atEnd;
        galleryPrev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        galleryNext.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    };

    galleryPrev.addEventListener(
        'click',
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const idx = mainSplide.index;
            if (idx > 0) mainSplide.go(idx - 1);
        },
        { signal }
    );

    galleryNext.addEventListener(
        'click',
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const end =
                mainSplide.Components?.Controller?.getEnd?.() ??
                Math.max(0, mainSplide.length - 1);
            const idx = mainSplide.index;
            if (idx < end) mainSplide.go(idx + 1);
        },
        { signal }
    );

    const onSplideIndexChange = (instance) => {
        instance.on('mounted', () => updateGalleryArrows());
        instance.on('moved', () => updateGalleryArrows());
        instance.on('updated', () => updateGalleryArrows());
        instance.on('refresh', () => updateGalleryArrows());
        instance.on('move', (_index, _prev, dest) => {
            if (typeof dest === 'number' && dest >= 0) updateGalleryArrows(dest);
        });
    };

    onSplideIndexChange(mainSplide);
    if (thumbSplide) onSplideIndexChange(thumbSplide);

    updateGalleryArrows();
    requestAnimationFrame(() => updateGalleryArrows());

    host._surgeUpdateGalleryArrows = updateGalleryArrows;
}

if (typeof window !== 'undefined') {
    window.surgeScrollSplideSwitcherActiveToLeadingEdge = surgeScrollSplideSwitcherActiveToLeadingEdge;
    window.surgeBindProductGalleryArrows = surgeBindProductGalleryArrows;
    window.surgeUpdateProductGalleryArrows = (host) => {
        if (host && typeof host._surgeUpdateGalleryArrows === 'function') {
            host._surgeUpdateGalleryArrows();
        }
    };
}

class SplideSlider extends HTMLElement {
    connectedCallback() {
        this.initialize()
    }

    disconnectedCallback() {
        try {
            this.syncSlider?.destroy?.(true);
        } catch {
            /* ignore */
        }
        try {
            this.slider?.destroy?.(true);
        } catch {
            /* ignore */
        }
        this.syncSlider = null;
        this.slider = null;
    }

    /**
     * Combined listings: swap gallery markup without removing this `<splide-slider>` from the DOM
     * (avoids disconnectedCallback + full custom-element reconnect). `sourceHost` may be from a
     * `DOMParser` document; `id` on this node is preserved.
     * @param {Element} sourceHost
     */
    reinitializeFrom(sourceHost) {
        if (!sourceHost || sourceHost.tagName !== 'SPLIDE-SLIDER') return;
        try {
            this.syncSlider?.destroy?.(true);
        } catch {
            /* ignore */
        }
        try {
            this.slider?.destroy?.(true);
        } catch {
            /* ignore */
        }
        this.syncSlider = null;
        this.slider = null;
        for (const attr of sourceHost.attributes) {
            if (attr.name === 'id') continue;
            this.setAttribute(attr.name, attr.value);
        }
        this.innerHTML = sourceHost.innerHTML;
        this.initialize();
    }

    initialize() {
        if (this.dataset.desktopHidden === 'wide' && window.screen.width > 991) return;
        this.slider = null;
        this.syncSlider = null;
        const initializeSplide = (element, options, autoMount = true) => {
            const splide = new Splide(element, options);
            if (this.dataset.progress === 'true') {
                var bar = splide.root.querySelector('[data-splide-progress]');
                splide.on('mounted move', function () {
                    var end = splide.Components.Controller.getEnd() + 1;
                    var rate = Math.min((splide.index + 1) / end, 1);
                    bar.style.width = String(100 * rate) + '%';
                });
            }

            const mountSplide = () => {
                splide.mount();
                const sliderList = this.querySelectorAll('.splide__list');
                sliderList.forEach(list => {
                    list.classList.remove('splide-slider--h-scroll');
                });
            }

            if (autoMount) {
                mountSplide();
            }

            splide.on("inactive", (slide) => SplideSlider.inactiveEvent(slide));
            if (this.dataset.refPicker === 'true' && window.screen.width > 991) {
                splide.on("active", (slide) => {
                    SplideSlider.goToReference(slide.index);
                });
            }
            if (this.dataset.destroyedUp) {
                if (screen.width > parseFloat(this.dataset.destroyedUp)) {
                    const slides = this.querySelectorAll('.splide__slide');
                    slides.forEach(slide => {
                        slide.removeAttribute('tabindex');
                        slide.querySelectorAll('a[tabindex="-1"], button[tabindex="-1"]').forEach(el => el.removeAttribute('tabindex'));
                    });
                }
            }
            return {
                splide,
                mount: mountSplide
            };
        };
        switch (this.dataset.sliderType) {
            case 'sync':
                const mpElem = this.querySelector('[data-main-thumbs]');
                const swElem = this.querySelector('[data-switcher-thumbs]');
                if (!mpElem || !swElem) return;
                const mpOpts = JSON.parse(mpElem.getAttribute('data-splide'));
                const swOpts = JSON.parse(swElem.getAttribute('data-splide'));
                const mpInstance = initializeSplide(mpElem, mpOpts, false);
                const swInstance = initializeSplide(swElem, swOpts, false);

                mpInstance.splide.sync(swInstance.splide);
                mpInstance.mount();
                swInstance.mount();

                let switcherScrollRaf = 0;
                const scheduleSwitcherScrollToTop = () => {
                    if (switcherScrollRaf) cancelAnimationFrame(switcherScrollRaf);
                    switcherScrollRaf = requestAnimationFrame(() => {
                        switcherScrollRaf = 0;
                        surgeScrollSplideSwitcherActiveToLeadingEdge(swElem, swInstance.splide);
                    });
                };
                swInstance.splide.on('mounted', scheduleSwitcherScrollToTop);
                swInstance.splide.on('moved', scheduleSwitcherScrollToTop);

                surgeBindProductGalleryArrows(this, mpInstance.splide, swInstance.splide);
                if (mpElem.classList.contains('product-gallery-main')) {
                    const mainSplide = mpInstance.splide;
                    const track = mpElem.querySelector('.splide__track');
                    if (track) {
                        const setMainTrackHeight = () => {
                            const slideEl = mainSplide.Components.Slides.getAt(mainSplide.index);
                            const el = slideEl && slideEl.slide ? slideEl.slide : mainSplide.Components.Elements.slides[mainSplide.index];
                            if (!el) return;
                            let h = el.offsetHeight;
                            if (!h && el.querySelector('img')) {
                                const img = el.querySelector('img');
                                if (img.naturalWidth && img.naturalHeight) h = (el.offsetWidth || img.offsetWidth) * (img.naturalHeight / img.naturalWidth);
                            }
                            if (h) track.style.setProperty('height', h + 'px', 'important');
                        };
                        const runAfterLayout = (fn) => {
                            requestAnimationFrame(() => requestAnimationFrame(fn));
                        };
                        mainSplide.on('move', () => runAfterLayout(setMainTrackHeight));
                        mainSplide.on('moved', setMainTrackHeight);
                        mainSplide.on('mounted', setMainTrackHeight);
                        mainSplide.on('resized', setMainTrackHeight);
                        runAfterLayout(setMainTrackHeight);
                        setTimeout(setMainTrackHeight, 100);
                        setTimeout(setMainTrackHeight, 400);
                    }
                    surgeInitProductGalleryLightbox(this, mainSplide);
                }
                this.slider = mpInstance.splide;
                this.syncSlider = swInstance.splide;
                break;
            case 'default':
                const elem = this.querySelector('.splide');
                const options = JSON.parse(elem.getAttribute('data-splide'));
                const instance = initializeSplide(elem, options, true);
                const splide = instance.splide;

                if (this.dataset.goTo === 'true') {
                    document.addEventListener('shopify:block:select', (event) => {
                        if (!event.target.classList.contains('splide__slide')) return;
                        const findSlideByBlockId = splide.Components.Slides.filter(`#${event.target.id}`)[0];
                        if (!findSlideByBlockId) return;
                        splide.go(findSlideByBlockId.index);
                    });
                }
                if (this.dataset.customArrows === 'true') SplideSlider.customArrows(splide);
                if (this.dataset.customPager === 'true') SplideSlider.customPager(splide);
                this.slider = splide;
                break;
            default:
                break;
        }
    }
    static inactiveEvent(currentSlide) {
        /* window.pauseElementBasedMedia(currentSlide.slide); */
    }

    static goToReference(currentIndex) {
        document.querySelector('[data-main-thumbs] .splide__list')
            ?.children[currentIndex]
            ?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
    }

    static customArrows(splide) {
        function goPrev() {
            splide.go(String.fromCharCode(60));
        }

        function goNext() {
            splide.go(String.fromCharCode(62));
        }

        function addListenersToActiveSlide() {
            const activeSlide = splide.Components.Elements.slides[splide.index];
            const prevButton = activeSlide.querySelector('[data-splide-prev]');
            const nextButton = activeSlide.querySelector('[data-splide-next]');
            if (prevButton) prevButton.addEventListener('click', goPrev);
            if (nextButton) nextButton.addEventListener('click', goNext);
        }

        function removeListenersFromInactiveSlide(slide) {
            const prevButton = slide.querySelector('[data-splide-prev]');
            const nextButton = slide.querySelector('[data-splide-next]');
            if (prevButton) prevButton.removeEventListener('click', goPrev);
            if (nextButton) nextButton.removeEventListener('click', goNext);
        }
        addListenersToActiveSlide();
        splide.on('active', addListenersToActiveSlide);
        splide.on('inactive', slide => removeListenersFromInactiveSlide(slide.slide));
    }

    static customPager(splide) {
        function goTo(event) {
            const index = Number(event.target.dataset.splidePager);
            splide.go(index);
        }

        function addListenersToActiveSlide() {
            const activeSlide = splide.Components.Elements.slides[splide.index];
            const buttons = activeSlide.querySelectorAll('[data-splide-pager]');
            buttons.forEach(button => button.addEventListener('click', goTo));
        }

        function removeListenersFromInactiveSlide(slide) {
            const buttons = slide.querySelectorAll('[data-splide-pager]');
            buttons.forEach(button => button.removeEventListener('click', goTo));
        }
        addListenersToActiveSlide();
        splide.on('active', addListenersToActiveSlide);
        splide.on('inactive', slide => removeListenersFromInactiveSlide(slide.slide));
    }
}
if (!customElements.get('splide-slider')) customElements.define('splide-slider', SplideSlider);

class SliderButton extends HTMLElement {
    connectedCallback() {
        const button = this.querySelector('button');
        if (!button) return;
        this.button = button;
        button.addEventListener('click', this.handleClick.bind(this));
        this.initObserver();
    }

    initObserver() {
        const sliderWrapper = document.querySelector(this.dataset.sliderTarget);
        if (!sliderWrapper) return;
        const setupSplide = () => {
            const splide = sliderWrapper.slider || sliderWrapper.querySelector('.splide')?.splide;
            if (splide) {
                this.splide = splide;
                splide.on('move', () => this.updateDisabledState());
                splide.on('mounted', () => this.updateDisabledState());
                splide.on('updated', () => this.updateDisabledState());
                this.updateDisabledState();
            } else {
                setTimeout(setupSplide, 100);
            }
        };
        setupSplide();
    }

    handleClick(event) {
        event.preventDefault();
        const splide = this.splide;
        if (!splide) return;
        if (this.querySelector('.splide__arrow--next')) {
            splide.go(String.fromCharCode(62));
        } else {
            splide.go(String.fromCharCode(60));
        }
    }

    updateDisabledState() {
        const splide = this.splide;
        if (!splide) return;
        const currentIndex = splide.index;
        const slideCount = splide.Components.Slides.getLength();
        const lastIndex = slideCount - 1;
        const isLoop = splide.options.type === 'loop';
        const isNext = this.querySelector('.splide__arrow--next');
        const isPrev = this.querySelector('.splide__arrow--prev');

        if (!isLoop) {
            if (isPrev) {
                this.button.disabled = currentIndex === 0;
            }
            if (isNext) {
                this.button.disabled = currentIndex === lastIndex;
            }
        } else {
            this.button.disabled = false;
        }
    }
}

if (!customElements.get('slider-button')) {
    customElements.define('slider-button', SliderButton);
}

function syncMainAndThumbs(sectionId) {
    const mainEl = document.querySelector(`#main-slider-${sectionId}`);
    const thumbsEl = document.querySelector(`#thumbs-slider-${sectionId}`);
    if (!mainEl || !thumbsEl) return;
    const main = mainEl.slider || mainEl.querySelector('.splide')?.splide;
    const thumbs = thumbsEl.slider || thumbsEl.querySelector('.splide')?.splide;
    if (!main || !thumbs) return;
    if (main._syncedWithThumbs) return;
    main._syncedWithThumbs = true;
    main.sync(thumbs);
    thumbs.sync(main);
    thumbs.on('click', (slide) => {
        main.go(slide.index);
    });
}
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[id^="main-slider-"]').forEach((mainEl) => {
        const sectionId = mainEl.id.replace('main-slider-', '');
        const check = setInterval(() => {
            syncMainAndThumbs(sectionId);
        }, 300);
        const stopWhenSynced = setInterval(() => {
            const main = document.querySelector(`#main-slider-${sectionId}`)?.slider;
            if (main?._syncedWithThumbs) {
                clearInterval(check);
                clearInterval(stopWhenSynced);
            }
        }, 500);
    });
});
